import { useEffect, useState } from 'react';
import { Grid, Card, CardHeader, CardContent, Typography } from '@mui/material';

import { columns } from './grid-columns';
import { FileObject, FileUploaded } from '../../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';
import { SiTrackGrid } from '../../../../components/si-track-grid/si-track-grid';
import { UploadFileForm } from './upload-file-form';
import { useFileDownload } from '../../../../components/file-download';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { EditFileUploadDialog } from './edit-file-upload.dialog';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { GridColumnConfig, GridPreference } from '../../../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../../../api/grid-api-key-store';
import { useSaveData } from '../../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../../hooks/use-debounce';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';

interface FileRequestArgs {
  projectID?: number;
  officeID?: number;
  customerID?: number;
  tempEntityID?: any;
}

interface FilesDialogProps {
  fileRequestArgs: FileRequestArgs;
  handleTempEntityID: (data: any) => void;
}

export const FileUpload = ({ fileRequestArgs, handleTempEntityID }: FilesDialogProps) => {
  const [fileReqArgs, setFileReqArgs] = useState(fileRequestArgs);
  const [editItem, setEditItem] = useState<any>();
  const { error, downloadFile, isLoading } = useFileDownload();

  const {
    isLoading: isFileLoading,
    isFetching: isFileFetching,
    data: fileData,
    refetch,
  } = useLoadData<ApiResponse<FileObject[]>>(fileUploadApiKeyStore.getFiles(fileReqArgs));

  const handleUploadedFile = (value: FileUploaded) => {
    setFileReqArgs({ ...fileReqArgs, tempEntityID: value.tempEntityID });
    setEditItem(undefined);
    handleTempEntityID(value.tempEntityID);
    refetch();
  };

  const handleDownloadFile = async (data: any) => {
    downloadFile({ additionalData: data });
  };

  const handleEdit = (value: any) => {
    setEditItem(value);
  };

  const updateFile = () => {
    setEditItem(undefined);
    refetch();
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Customer', 'File'));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredFiles, setFilteredFiles] = useState<FileObject[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredFileList = fileData?.data?.filter((file) =>
      Object.values(file).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredFiles(filteredFileList || []);
  }, [debouncedSearchTerm, fileData?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'File',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'File',
      pageName: 'Customer',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader className="subHead" title={<Typography variant="h3">UPLOAD FILES</Typography>} />
      <CardContent className="formContainer">
        <Grid container spacing={2}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <UploadFileForm fileRequestArgs={fileReqArgs} onUploaded={handleUploadedFile} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <LoadingIndicator isLoading={isLoadingGridLayout || isFileLoading} />
            <SiTrackGrid
            fileTitle='customer_file'
              // dataItems={fileData?.data}
              dataItemKey="fileID"
              columns={columns}
              onDownload={(data: any) => {
                handleDownloadFile(data);
              }}
              onEdit={handleEdit}
              actionPermissions={{
                edit: UserPermissionName.EDIT_FILE,
                delete: UserPermissionName.DELETE_FILE,
                download: UserPermissionName.DOWNLOAD_FILE,
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              deleteMutationConfig={(item) => fileUploadApiKeyStore.deleteFile(item.fileID)}
              isBusy={isFileLoading || isFileFetching || isLoading}
              // -----------------Grid Layout-------
              dataItems={filteredFiles || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
            />
          </Grid>
        </Grid>
        {editItem && (
          <EditFileUploadDialog
            fileData={editItem}
            fileRequestArgs={fileReqArgs}
            onUpdate={updateFile}
            onClosed={() => setEditItem(undefined)}
          />
        )}
      </CardContent>
    </Card>
  );
};
